import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import { useAuth } from "./auth";
import { membershipRoleLabel } from "./types";
import type { Membership } from "./types";
import { panelById, panelIdForRole, type PanelId } from "./panels";

/**
 * "Access denied" screen shared by every admin panel.
 *
 * Lists EVERY active membership the user holds — not just the ones matching
 * the current panel — grouped by the panel that serves each role, so a
 * multi-role user who lands on the wrong site can get to the right one in a
 * single click. Pharmacist memberships get a plain "use the mobile app" note
 * instead of a dead-end button.
 */
export function AccessDeniedScreen({ requiredRole }: { requiredRole: string }) {
  const { user, allMemberships, membership, logout, switchMembershipAndNavigate, panel } =
    useAuth();
  const [switchingId, setSwitchingId] = useState<string | null>(null);
  const [switchError, setSwitchError] = useState<string | null>(null);

  const handleSwitch = async (m: Membership) => {
    setSwitchError(null);
    setSwitchingId(m.membershipId ?? null);
    try {
      await switchMembershipAndNavigate(m);
    } catch (err) {
      console.error("[AccessDenied] switch failed", err);
      setSwitchError(
        err instanceof Error ? err.message : "Failed to switch role. Please try again.",
      );
      setSwitchingId(null);
    }
  };

  const active = allMemberships.filter(
    (m) =>
      m.status === "active" &&
      m.membershipId !== membership?.membershipId &&
      m.role !== "pharmacist",
  );
  const groups = groupByPanel(active);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 3,
      }}
    >
      <Box sx={{ maxWidth: 460, textAlign: "center", width: "100%" }}>
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 1.5 }}>
          Access denied
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Your active role does not have <code>{requiredRole}</code> access to this panel.
        </Typography>
        {user?.email && (
          <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 2 }}>
            Signed in as <code>{user.email}</code>
          </Typography>
        )}

        {active.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            You have no other active roles to switch to.
          </Typography>
        )}

        <MembershipSwitchList
          groups={groups}
          currentPanel={panel}
          switchingId={switchingId}
          onSwitch={handleSwitch}
        />


        {switchError && (
          <Alert severity="error" sx={{ mb: 2, textAlign: "left" }}>
            {switchError}
          </Alert>
        )}

        <Button variant="outlined" onClick={() => logout()}>
          Sign out
        </Button>
      </Box>
    </Box>
  );
}

export interface PanelGroup {
  panel: PanelId;
  label: string;
  memberships: Membership[];
}

/** Group memberships by the panel that serves their role. */
export function groupByPanel(memberships: Membership[]): PanelGroup[] {
  const order: PanelId[] = ["katana-admin", "chain-admin", "pharmacy-admin"];
  return order
    .map((panel) => ({
      panel,
      label: panelById(panel).label,
      memberships: memberships.filter((m) => panelIdForRole(m.role) === panel),
    }))
    .filter((g) => g.memberships.length > 0);
}

export function MembershipSwitchList({
  groups,
  currentPanel,
  switchingId,
  onSwitch,
}: {
  groups: PanelGroup[];
  currentPanel: PanelId;
  switchingId: string | null;
  onSwitch: (m: Membership) => void;
}) {
  if (groups.length === 0) return null;
  return (
    <Box sx={{ mb: 2, textAlign: "left" }}>
      {groups.map((g) => (
        <Box key={g.panel} sx={{ mb: 1.5 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontWeight: 600, display: "block", mb: 0.5 }}
          >
            {g.panel === currentPanel ? `${g.label} (this panel)` : g.label}
          </Typography>
          <Stack spacing={1}>
            {g.memberships.map((m) => (
              <Button
                key={m.membershipId}
                variant="contained"
                size="small"
                disabled={switchingId !== null}
                startIcon={
                  switchingId === m.membershipId ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : undefined
                }
                onClick={() => onSwitch(m)}
              >
                {membershipRoleLabel(m.role, m.chainId, m.pharmacyId)}
              </Button>
            ))}
          </Stack>
          <Divider sx={{ mt: 1.5, borderStyle: "dashed" }} />
        </Box>
      ))}
    </Box>
  );
}
